import {
  AdminBookingStateEnum,
  BookingSourceAbbreviationEnum,
} from '@koob/enums';

/**
 * KD-3842 client folder bookings list: one list grouped by trip, hotels and
 * experiences booked outside a trip under "Standalone". Pure functions, shared
 * by the TO and DMC apps, which build their own table from the result.
 */

export const FolderRowTypeEnum = {
  TRIP: 'trip',
  HOTEL: 'hotel',
  EXPERIENCE: 'experience',
};

export const FolderTripStatusEnum = {
  DRAFT: 'draft',
  BOOKED: 'booked',
};

export const FOLDER_ROWS_ALL = 'all';

export const FolderServiceModeEnum = {
  LIVE: 'live',
  ON_REQUEST: 'on_request',
};

// AdminBookingDmcStateEnum.INSTANT_BOOKING on the API side.
const INSTANT_BOOKING_DMC_STATE = 'instant_booking';

const DAY_MS = 24 * 60 * 60 * 1000;

// A date the browser cannot parse becomes null instead of a NaN downstream.
const validDate = (value) =>
  value && Number.isFinite(new Date(value).getTime()) ? value : null;

const nightsBetween = (startAt, endAt) => {
  if (!startAt || !endAt) return null;
  const nights = Math.round(
    (new Date(endAt).getTime() - new Date(startAt).getTime()) / DAY_MS,
  );
  return Number.isFinite(nights) && nights > 0 ? nights : null;
};

// Left out of every amount: a cancelled, refused or failed booking costs
// nothing, and a replaced one is superseded by its replacement.
const EXCLUDED_FROM_TOTALS = [
  AdminBookingStateEnum.CANCELLED,
  AdminBookingStateEnum.REFUSED,
  AdminBookingStateEnum.FAILED,
  AdminBookingStateEnum.REPLACED,
];

/**
 * The state a booking is filtered on: pending_source is a pending booking.
 * Every other state, pre_confirmed included, stands on its own.
 */
export const getFolderServiceFilterStatus = (state) =>
  state === AdminBookingStateEnum.PENDING_SOURCE
    ? AdminBookingStateEnum.PENDING
    : state;

const sumAmounts = (rows) =>
  rows.reduce((total, row) => total + (row.amount ?? 0), 0);

/**
 * KD-3903: a folder can hold bookings made in several currencies. Amounts are
 * never converted: they add up per currency, as [{ currency, amount }] in the
 * order the currencies first appear.
 */
const addByCurrency = (byCurrency, { currency, amount }) => {
  if (amount === null || amount === undefined) return byCurrency;
  const key = currency ?? null;
  byCurrency.set(key, (byCurrency.get(key) ?? 0) + amount);
  return byCurrency;
};

const toCurrencyAmounts = (byCurrency) =>
  [...byCurrency].map(([currency, amount]) => ({ currency, amount }));

/** Sums rows' `amounts` per currency. */
const sumByCurrency = (rows) =>
  toCurrencyAmounts(
    rows.flatMap((row) => row.amounts ?? []).reduce(addByCurrency, new Map()),
  );

const countedInTotals = (service) => service.countsInTotals;

/**
 * Booking price as the folder page shows it: the price the folder's
 * organization pays (totalPrice) + adjustments. Never sourceTotalPrice, the
 * DMC's net price, which the API keeps from a TO (KD-3873). The API sends 0
 * when it has no price, shown as unpriced ("--").
 */
const serviceAmount = (booking) =>
  booking?.totalPrice ? booking.totalPrice + serviceAdjustment(booking) : null;

/** Adjustments of a priced booking and of its extras; 0 when unpriced. */
const serviceAdjustment = (booking) => {
  if (!booking?.totalPrice) {
    return 0;
  }
  const extrasAdjustment = (booking.experiencesBooked ?? [])
    .flatMap((experience) => experience.extras ?? [])
    .reduce(
      (total, extra) => total + (extra?.pricingSummary?.adjustmentAmount ?? 0),
      0,
    );

  return (booking.pricingSummary?.adjustmentAmount ?? 0) + extrasAdjustment;
};

// Names of the travelers a booking is for: its rooms', or its experience's.
const bookingTravelerNames = (booking) =>
  [
    ...(booking?.roomsBooked ?? []).flatMap((room) => room?.travelers ?? []),
    ...(booking?.experiencesBooked ?? []).flatMap(
      (experience) => experience?.travelers ?? [],
    ),
  ].map((traveler) =>
    `${traveler?.firstName ?? ''} ${traveler?.lastName ?? ''}`.trim(),
  );

const amountsOf = (amount, currency) =>
  amount === null || amount === undefined
    ? []
    : [{ currency: currency ?? null, amount }];

/** A hotel or experience booking of the folder, normalized for the list. */
export const toFolderService = (booking) => {
  const isHotel = Boolean(booking?.hotel);
  const experience = booking?.experiencesBooked?.[0]?.experience;
  const tripId = booking?.trip?.id ?? null;

  let provider = experience?.organizationName ?? null;
  if (isHotel) {
    provider =
      booking.kind === 'dmc'
        ? (booking.hotel?.organization?.displayName ?? null)
        : (booking.kind ?? null);
  }
  const rooms = booking?.roomsBooked ?? [];
  const amount = serviceAmount(booking);

  return {
    id: booking?.id,
    booking,
    type: isHotel ? FolderRowTypeEnum.HOTEL : FolderRowTypeEnum.EXPERIENCE,
    experienceType: isHotel ? null : (experience?.type ?? null),
    mode:
      booking?.dmcState === INSTANT_BOOKING_DMC_STATE
        ? FolderServiceModeEnum.LIVE
        : FolderServiceModeEnum.ON_REQUEST,
    roomName: isHotel ? (rooms[0]?.room?.name ?? null) : null,
    roomCount: isHotel ? rooms.length : 0,
    nights: isHotel ? nightsBetween(booking?.startAt, booking?.endAt) : null,
    pax: isHotel
      ? null
      : (booking?.experiencesBooked?.[0]?.travelers?.length ?? null),
    toReference: booking?.toReference ?? null,
    guaranteedDeparture: Boolean(booking?.guaranteedDeparture),
    name: isHotel ? booking.hotel?.displayName : experience?.name,
    city: isHotel
      ? (booking.hotel?.city?.title ?? booking?.city?.title ?? null)
      : (experience?.inCity?.title ?? null),
    provider,
    travelerNames: bookingTravelerNames(booking),
    status: booking?.state,
    filterStatus: getFolderServiceFilterStatus(booking?.state),
    tripId,
    source: tripId
      ? BookingSourceAbbreviationEnum.TRIP_DESIGNER
      : BookingSourceAbbreviationEnum.MARKETPLACE,
    bookedAt: booking?.createdAt ?? null,
    startAt: validDate(booking?.startAt),
    endAt: validDate(booking?.endAt),
    amount,
    adjustmentAmount: serviceAdjustment(booking),
    currency: booking?.currency ?? null,
    amounts: amountsOf(amount, booking?.currency),
    countsInTotals: !EXCLUDED_FROM_TOTALS.includes(booking?.state),
  };
};

// A draft without a price stays unpriced unless an adjustment applies to it.
// The DMC Trip Designer stores the price as a string ("1578.00").
const draftAmount = (draftPrice, adjustmentAmount) => {
  const price =
    draftPrice === undefined || draftPrice === null || draftPrice === ''
      ? NaN
      : Number(draftPrice);
  if (Number.isNaN(price)) {
    return adjustmentAmount ? adjustmentAmount : null;
  }
  return price + adjustmentAmount;
};

/**
 * A trip of the folder with its services. A draft trip lists no service
 * (they show once it is booked) and costs its draft price; a booked trip
 * costs the sum of its services. Both add the adjustments applied to the trip
 * itself, like the trip pricing details page, so the amounts match it.
 */
export const toFolderTrip = (trip, services) => {
  const draft = Boolean(trip?.draft);
  const adjustmentAmount = Number(trip?.adjustmentAmount ?? 0);
  const items = draft
    ? []
    : services.filter((service) => String(service.tripId) === String(trip.id));
  const bookedDates = items
    .map((service) => service.bookedAt)
    .filter(Boolean)
    .sort();
  const counted = items.filter(countedInTotals);
  // A trip's adjustments are in its own currency: its draft price's, or, once
  // booked, its services' (one DMC, so one currency).
  const currency = draft
    ? (trip?.draftPrice?.currency ?? null)
    : (counted[0]?.currency ?? items[0]?.currency ?? null);
  const amount = draft
    ? draftAmount(trip?.draftPrice?.totalPrice, adjustmentAmount)
    : sumAmounts(counted) + adjustmentAmount;
  const amounts = draft
    ? amountsOf(amount, currency)
    : sumByCurrency([
        ...counted,
        { amounts: amountsOf(adjustmentAmount || null, currency) },
      ]);

  return {
    id: trip?.id,
    trip,
    type: FolderRowTypeEnum.TRIP,
    name: trip?.name,
    toReference: trip?.toReference ?? null,
    isProposal: Boolean(trip?.isProposal),
    hotelsCount: items.filter((s) => s.type === FolderRowTypeEnum.HOTEL).length,
    experiencesCount: items.filter(
      (s) => s.type === FolderRowTypeEnum.EXPERIENCE,
    ).length,
    startLocation: trip?.startLocation?.title ?? null,
    endLocation: trip?.endLocation?.title ?? null,
    provider: trip?.dmc?.displayName ?? null,
    status: draft ? FolderTripStatusEnum.DRAFT : FolderTripStatusEnum.BOOKED,
    draft,
    expandable: !draft,
    items,
    bookedAt: draft ? null : (bookedDates[0] ?? null),
    startAt: validDate(trip?.startDate),
    endAt: validDate(trip?.endDate),
    adjustmentAmount,
    currency,
    // ponytail: `amount` mixes currencies on a trip booked in several; it
    // only sorts the list, `amounts` is what shows.
    amount,
    amounts,
  };
};

const matchesQuery = (values, query) => {
  const needle = query?.trim().toLowerCase();
  if (!needle) return true;
  return values.filter(Boolean).join(' ').toLowerCase().includes(needle);
};

/**
 * @param {object} folder the folder from GET /folders/:id
 * @param {{ type?: string, status?: string, query?: string }} filters type is
 *   all / trip / hotel / experience, status a trip status or a booking state,
 *   query searched in the service, its travelers, city and provider
 */
export const buildFolderBookingList = (
  folder,
  { type = FOLDER_ROWS_ALL, status = FOLDER_ROWS_ALL, query = '' } = {},
) => {
  const services = (folder?.bookings ?? []).map(toFolderService);
  const trips = (folder?.trips ?? []).map((trip) =>
    toFolderTrip(trip, services),
  );
  const tripIds = new Set(trips.map((trip) => String(trip.id)));
  const standalone = services.filter(
    (service) => !service.tripId || !tripIds.has(String(service.tripId)),
  );
  const listed = [...trips.flatMap((trip) => trip.items), ...standalone];
  const listedOfType = (rowType) =>
    listed.filter((service) => service.type === rowType);

  const counts = {
    [FolderRowTypeEnum.TRIP]: trips.length,
    [FolderRowTypeEnum.HOTEL]: listedOfType(FolderRowTypeEnum.HOTEL).length,
    [FolderRowTypeEnum.EXPERIENCE]: listedOfType(FolderRowTypeEnum.EXPERIENCE)
      .length,
    [FOLDER_ROWS_ALL]: trips.length + listed.length,
  };

  // One subtotal per currency, in the order the currencies first appear.
  const totalsByCurrency = new Map();
  const addTo =
    (part) =>
    ({ currency, amount }, adjustment = 0) => {
      const key = currency ?? null;
      const totals = totalsByCurrency.get(key) ?? {
        currency: key,
        hotels: 0,
        experiences: 0,
        draftTrips: 0,
        adjustments: 0,
        total: 0,
        // KD-3910: the split production shows, every adjustment apart from
        // the prices it applies to. Its four figures add up to the total.
        priceSummary: {
          hotels: 0,
          experiences: 0,
          draftTrips: 0,
          adjustments: 0,
        },
      };
      totals[part] += amount;
      totals.total += amount;
      totals.priceSummary[part] += amount - adjustment;
      totals.priceSummary.adjustments += adjustment;
      totalsByCurrency.set(key, totals);
    };
  // A row's own adjustments are in its currency, so they split per currency.
  const addRows = (part, rows) =>
    rows.forEach((row) =>
      row.amounts.forEach((amount) =>
        addTo(part)(amount, row.adjustmentAmount),
      ),
    );
  addRows(
    'hotels',
    listedOfType(FolderRowTypeEnum.HOTEL).filter(countedInTotals),
  );
  addRows(
    'experiences',
    listedOfType(FolderRowTypeEnum.EXPERIENCE).filter(countedInTotals),
  );
  addRows(
    'draftTrips',
    trips.filter((trip) => trip.draft),
  );
  // Trip-level adjustments of booked trips; a draft carries its own above.
  trips
    .filter((trip) => !trip.draft && trip.adjustmentAmount)
    .forEach((trip) =>
      addTo('adjustments')({
        currency: trip.currency,
        amount: trip.adjustmentAmount,
      }),
    );
  const totals = [...totalsByCurrency.values()];

  const unfiltered =
    type === FOLDER_ROWS_ALL && status === FOLDER_ROWS_ALL && !query?.trim();
  const serviceMatches = (service) =>
    (type === FOLDER_ROWS_ALL || type === service.type) &&
    (status === FOLDER_ROWS_ALL || status === service.filterStatus) &&
    matchesQuery(
      [service.name, ...service.travelerNames, service.city, service.provider],
      query,
    );
  const tripMatches = (trip) =>
    (type === FOLDER_ROWS_ALL || type === FolderRowTypeEnum.TRIP) &&
    (status === FOLDER_ROWS_ALL || status === trip.status) &&
    matchesQuery(
      [trip.name, trip.startLocation, trip.endLocation, trip.provider],
      query,
    );

  const visibleTrips = trips
    .map((trip) => {
      // A trip matching the filter itself (name, route, status) keeps all its
      // services, collapsed; one kept only through some of its services shows
      // expanded with those services alone.
      const tripHit = tripMatches(trip);
      const items = tripHit ? trip.items : trip.items.filter(serviceMatches);
      if (!tripHit && items.length === 0) return null;

      return { ...trip, items, forceExpanded: !tripHit };
    })
    .filter(Boolean);
  const visibleStandalone = standalone.filter(serviceMatches);

  const filteredTotals = sumByCurrency([
    // A matching trip counts in full (its adjustment included); a trip kept
    // through some services adds those services only.
    ...visibleTrips.flatMap((trip) =>
      trip.forceExpanded ? trip.items.filter(countedInTotals) : [trip],
    ),
    ...visibleStandalone.filter(countedInTotals),
  ]);

  return {
    trips: visibleTrips,
    standalone: visibleStandalone,
    counts,
    totals,
    filteredTotals,
    // Rows on screen before any manual expand: collapsed trips hide theirs.
    shownCount:
      visibleTrips.length +
      visibleTrips.reduce(
        (total, trip) => total + (trip.forceExpanded ? trip.items.length : 0),
        0,
      ) +
      visibleStandalone.length,
  };
};

/** Columns the bookings list can be sorted on. */
export const FolderRowSortKeyEnum = {
  ID: 'id',
  NAME: 'name',
  TYPE: 'type',
  CITY: 'city',
  TIR: 'tir',
  BOOKED_ON: 'bookedOn',
  DATES: 'dates',
  PROVIDER: 'provider',
  SOURCE: 'source',
  STATUS: 'status',
  GD: 'gd',
  AMOUNT: 'amount',
};

const TYPE_ORDER = [
  FolderRowTypeEnum.TRIP,
  FolderRowTypeEnum.HOTEL,
  FolderRowTypeEnum.EXPERIENCE,
];

const lower = (value) => (value ? String(value).toLowerCase() : null);

// Ids are int8 digit strings: zero-padded, they sort in numeric order with no
// precision lost past 2^53 (Number would), and '10' stays after '9'.
const idSortValue = (id) =>
  /^\d+$/.test(String(id ?? '')) ? String(id).padStart(20, '0') : null;

const SORT_VALUES = {
  [FolderRowSortKeyEnum.ID]: (row) => idSortValue(row.id),
  [FolderRowSortKeyEnum.NAME]: (row) => lower(row.name),
  [FolderRowSortKeyEnum.TYPE]: (row) => TYPE_ORDER.indexOf(row.type),
  [FolderRowSortKeyEnum.CITY]: (row) => lower(row.city ?? row.startLocation),
  [FolderRowSortKeyEnum.TIR]: (row) => lower(row.toReference),
  [FolderRowSortKeyEnum.BOOKED_ON]: (row) => row.bookedAt,
  [FolderRowSortKeyEnum.DATES]: (row) => row.startAt,
  [FolderRowSortKeyEnum.PROVIDER]: (row) => lower(row.provider),
  [FolderRowSortKeyEnum.SOURCE]: (row) => row.source ?? null,
  [FolderRowSortKeyEnum.STATUS]: (row) => row.status ?? null,
  [FolderRowSortKeyEnum.GD]: (row) => (row.guaranteedDeparture ? 1 : 0),
  [FolderRowSortKeyEnum.AMOUNT]: (row) => row.amount,
};

const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

/**
 * Sorts the list level by level so the grouping survives: trips among trips,
 * services inside their trip, standalone services among themselves. Empty
 * values always sink to the bottom.
 *
 * @param {{ trips: object[], standalone: object[] }} list from buildFolderBookingList
 * @param {{ key: string, direction: 'asc' | 'desc' } | null} sort
 */
export const sortFolderBookingList = (list, sort) => {
  const sortValue = sort && SORT_VALUES[sort.key];
  if (!sortValue) return list;

  const direction = sort.direction === 'desc' ? -1 : 1;
  const sortRows = (rows) =>
    [...rows].sort((a, b) => {
      const valueA = sortValue(a);
      const valueB = sortValue(b);
      if (valueA == null || valueB == null) {
        return compareValues(valueA, valueB);
      }
      return direction * compareValues(valueA, valueB);
    });

  return {
    ...list,
    trips: sortRows(list.trips).map((trip) => ({
      ...trip,
      items: sortRows(trip.items),
    })),
    standalone: sortRows(list.standalone),
  };
};
